import { axiosInstance } from './api'

const API_BASE_URL = process.env.REACT_APP_NEW_BASE_V4_URL_MONITORING

/**
 * Fetch organization data from monitoring-microservice
 * Uses the /organizations/by-code/:organizationCode endpoint
 * 
 * @param {string|number} organizationCode - The organization code (numeric ID like 885566339)
 * @returns {Promise<{success: boolean, data: {organizationName: string, address: string, gstNumber: string}, source: string}>}
 */
export const fetchOrganizationData = async (organizationCode) => {
  if (!organizationCode) {
    return {
      success: false,
      error: 'Organization code is required',
      data: null
    }
  }

  try {
    console.log(`🏢 Fetching organization from V4 API for code: ${organizationCode}`)

    //Use the V4 organization endpoint in monitoring-microservice
    const response = await axiosInstance.get(`${API_BASE_URL}organizations/by-code/${organizationCode}`, {
      timeout: 5000
    })

    console.log('📦 V4 Organization API response:', response.data)

    if (response.data.success && response.data.data) {
      const org = response.data.data

      const organizationData = {
        organizationName: org.organizationName || '',
        address: org.address || '',
        gstNumber: org.gstNumber || '',
        email: org.email || '',
        phone: org.phone || '',
        panNumber: org.panNumber || '',
        businessInfo: org.businessInfo || null
      }

      console.log('✅ Organization data fetched from V4:', organizationData)
      return {
        success: true,
        data: organizationData,
        source: 'v4-database'
      }
    }

    // No data found
    console.error('❌ Organization not found for code:', organizationCode)
    return {
      success: false,
      error: `Organization with code ${organizationCode} not found`,
      data: null
    }

  } catch (error) {
    console.error('❌ V4 Organization API error:', error.message)
    if (error.response) {
      console.error('Response status:', error.response.status)
      console.error('Response data:', error.response.data)
    }
    return {
      success: false,
      error: `Failed to fetch organization data: ${error.message}`,
      data: null
    }
  }
}

/**
 * Fetch organization data by MongoDB ObjectId
 * Uses the /organizations/:id endpoint
 * 
 * @param {string} organizationId - MongoDB ObjectId of the organization
 * @returns {Promise<{success: boolean, data: object}>}
 */
export const fetchOrganizationById = async (organizationId) => {
  if (!organizationId) {
    return {
      success: false,
      error: 'Organization ID is required',
      data: null
    }
  }

  try {
    console.log(`🏢 Fetching organization by ID from V4: ${organizationId}`)

    const response = await axiosInstance.get(`${API_BASE_URL}organizations/${organizationId}`, {
      timeout: 5000
    })

    console.log('📦 V4 Organization by ID response:', response.data)

    if (response.data.success && response.data.data) {
      const org = response.data.data

      const organizationData = {
        organizationName: org.organizationName || '',
        organizationCode: org.organizationCode || '',
        address: org.address || '',
        gstNumber: org.gstNumber || '',
        email: org.email || '',
        phone: org.phone || '',
        panNumber: org.panNumber || '',
        businessInfo: org.businessInfo || null
      }

      console.log('✅ Organization data fetched by ID:', organizationData)
      return {
        success: true,
        data: organizationData,
        source: 'v4-database'
      }
    }

    return {
      success: false,
      error: 'Organization not found',
      data: null
    }

  } catch (error) {
    console.error('❌ Error fetching organization by ID:', error.message)
    return {
      success: false,
      error: `Failed to fetch organization: ${error.message}`,
      data: null
    }
  }
}

/**
 * Search organizations
 * Uses the /organizations endpoint with query params
 * 
 * @param {object} params - Search parameters
 * @param {string} params.search - Search term for organization name
 * @param {string} params.organizationCode - Filter by organization code
 * @returns {Promise<{success: boolean, data: array}>}
 */
export const searchOrganizations = async (params = {}) => {
  try {
    console.log(`🔍 Searching organizations with params:`, params)

    const response = await axiosInstance.get(`${API_BASE_URL}organizations`, {
      params,
      timeout: 5000
    })

    console.log('📦 Organization search response:', response.data)

    if (response.data.success && response.data.data) {
      return {
        success: true,
        data: response.data.data,
        total: response.data.total
      }
    }

    return {
      success: false,
      error: 'No organizations found',
      data: []
    }

  } catch (error) {
    console.error('❌ Error searching organizations:', error.message)
    return {
      success: false,
      error: `Failed to search organizations: ${error.message}`,
      data: []
    }
  }
}

/**
 * Fetch Zoho organization data
 * Uses the /zoho/data endpoint
 * 
 * @param {string} organizationId - The Zoho organization ID
 * @returns {Promise<{success: boolean, data: object}>}
 */
export const fetchZohoOrganizationData = async (organizationId) => {
  if (!organizationId) {
    return {
      success: false,
      error: 'Organization ID is required',
      data: null
    }
  }

  try {
    console.log(`🏢 Fetching Zoho data for organization ID: ${organizationId}`)

    const response = await axiosInstance.get(`${API_BASE_URL}zoho/data`, {
      params: { organizationId },
      timeout: 10000
    })

    console.log('📦 Zoho API response:', response.data)

    return response.data
  } catch (error) {
    console.error('❌ Zoho API error:', error.message)
    return {
      success: false,
      error: error.response?.data?.message || `Failed to fetch Zoho data: ${error.message}`,
      data: null
    }
  }
}
